import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { Shield } from "lucide-react";

const CLASSIFICATIONS: { value: string; label: string }[] = [
  { value: "sole_proprietor", label: "Sole Proprietor" },
  { value: "single_member_llc", label: "Single-Member LLC" },
  { value: "partnership", label: "Partnership" },
  { value: "corporation", label: "Corporation" },
  { value: "s_corp", label: "S-Corp" },
  { value: "c_corp", label: "C-Corp" },
  { value: "nonprofit", label: "Nonprofit" },
  { value: "other", label: "Other" },
];

function maskTin(last4?: string | null, type?: string | null): string {
  if (!last4) return "—";
  return type === "ein" ? `XX-XXX${last4}` : `***-**-${last4}`;
}

function statusFromInstructor(status: string | null | undefined): string {
  if (!status) return "Missing";
  const s = String(status);
  if (/verif/i.test(s)) return "Verified";
  if (/receiv|on file/i.test(s)) return "Received";
  if (/request/i.test(s)) return "Requested";
  return "Missing";
}

export default function TaxInformationSection({
  instructor, w9Request, onChanged,
}: { instructor: any; w9Request: any; onChanged: () => void }) {
  const [classification, setClassification] = useState<string>(w9Request?.tax_classification ?? "");
  const [saving, setSaving] = useState(false);

  const saveClassification = async () => {
    if (!w9Request?.id) {
      toast({ title: "No W-9 record yet", description: "Request or receive a W-9 first from the Contractor & Tax section." });
      return;
    }
    setSaving(true);
    const { error } = await (supabase.from as any)("instructor_w9_requests")
      .update({ tax_classification: classification || null })
      .eq("id", w9Request.id);
    setSaving(false);
    if (error) { toast({ title: "Could not save", description: error.message, variant: "destructive" }); return; }
    toast({ title: "Tax classification saved" });
    onChanged();
  };

  const displayStatus = w9Request
    ? (w9Request.verification_status === "verified" ? "Verified"
      : w9Request.completed_at ? "Received"
      : w9Request.requested_at ? "Requested" : "Missing")
    : statusFromInstructor(instructor.w9_status);

  const dateReq = w9Request?.requested_at ?? instructor.w9_requested_at ?? null;
  const dateRec = w9Request?.completed_at ?? instructor.w9_received_at ?? null;
  const dateVer = w9Request?.verified_at ?? null;
  const verifiedBy = w9Request?.verified_by ?? null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Shield className="h-4 w-4" /> Tax Information
        </CardTitle>
        <CardDescription>
          Taxpayer identifiers are encrypted at rest and only the last four digits are ever shown.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div><Label className="text-xs">W-9 Status</Label><div className="font-medium">{displayStatus}</div></div>
          <div><Label className="text-xs">Date Requested</Label><div>{dateReq ? new Date(dateReq).toLocaleDateString() : "—"}</div></div>
          <div><Label className="text-xs">Date Received</Label><div>{dateRec ? new Date(dateRec).toLocaleDateString() : "—"}</div></div>
          <div><Label className="text-xs">Date Verified</Label><div>{dateVer ? new Date(dateVer).toLocaleDateString() : "—"}</div></div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Taxpayer ID</Label>
            <div className="font-mono">{maskTin(w9Request?.tin_last4, w9Request?.tin_type)}</div>
            <div className="text-[11px] text-muted-foreground">Full number never displayed.</div>
          </div>
          <div>
            <Label className="text-xs">ID Type</Label>
            <div className="uppercase">{w9Request?.tin_type ?? "—"}</div>
          </div>
          <div>
            <Label className="text-xs">Verified By</Label>
            <div>{verifiedBy ? verifiedBy.slice(0, 8) + "…" : "—"}</div>
          </div>
          <div>
            <Label className="text-xs">Collection Method</Label>
            <div className="capitalize">{w9Request?.collection_method?.replace(/_/g, " ") ?? "—"}</div>
          </div>
        </div>

        <div className="border-t pt-3 grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div className="md:col-span-2">
            <Label className="text-xs">Tax Classification</Label>
            <Select value={classification} onValueChange={setClassification}>
              <SelectTrigger><SelectValue placeholder="Select classification" /></SelectTrigger>
              <SelectContent>
                {CLASSIFICATIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Button size="sm" onClick={saveClassification} disabled={saving || !w9Request?.id}>Save</Button>
          </div>
        </div>

        <div className="rounded-md border p-3 bg-muted/30 text-xs text-muted-foreground">
          W-9 uploads, replacements, and downloads live in the existing Contractor &amp; Tax section of the profile and go through the audited admin-w9-access edge function. Full TIN is not exposed anywhere in the admin UI.
        </div>
      </CardContent>
    </Card>
  );
}
